/**
 * packages/shared/src/fmv/browse.ts
 *
 * SIL-844 follow-up (public FMV browse flow): the manufacturer -> product
 * line -> asset class -> model interaction settled by
 * apps/customer/src/components/shared/MakeModelPicker.tsx (SIL-953, branch
 * jarriett/sku-family-picker-a6) is reproduced here as framework-free
 * derivation logic — formatting, dedup/extraction, and the two gate
 * predicates — so the customer portal's picker and the public widget
 * (apps/fmv, vanilla TS, no React) share ONE definition and cannot drift.
 * Both are thin view layers over this module; neither re-derives these
 * rules independently.
 *
 * IMPORTANT — pricing rules differ from the portal, on purpose (public FMV
 * plan, "What we publish"):
 *   - A "line" (product line) row's family range is free to view, no lookup
 *     consumed — `formatRangeText` below is what renders it.
 *   - A "model" row (a specific catalog SKU) carries NO price at the list
 *     stage in the public widget — its price is only ever resolved via the
 *     existing paid POST /api/pub/fmv/estimate call. This module never
 *     assumes a model row has a price field at all; see
 *     `apps/fmv/src/browse.ts` for how the widget wires that.
 */

/** A root row — a `grain='master'` SKU (its variants point at it via
 * parent_sku_id) or a standalone `grain='variant'` root SKU with no
 * children. Mirrors GET /api/client/model-suggest's root shape and this
 * feature's GET /api/pub/fmv/browse/lines response. Deliberately carries NO
 * price fields — see module header; the free family range is fetched
 * separately, one line at a time, via the per-line detail call. */
export interface FmvBrowseLine {
  skuId: string;
  modelName: string;
  displayName: string | null;
  assetClassId: string | null;
  grain: 'master' | 'variant';
}

/** A single line's free-to-view detail — the family range (masters only)
 * plus the live/priced-variant counts a browse UI needs to label the line
 * honestly. Fetched one skuId at a time via the per-line detail endpoint,
 * never batched into the multi-line list (see this module's header + the
 * per-line-detail-only shape requirement in the public browse API). */
export interface FmvBrowseLineDetail extends FmvBrowseLine {
  rangeLowUsd: number | null;
  rangeHighUsd: number | null;
  pricedVariantCount: number | null;
}

/** A model row — a specific catalog SKU, name only. See module header:
 * neither this type nor any function below ever attaches a price to a
 * model row; the public widget resolves a model's price exclusively via
 * POST /estimate after the visitor picks it. */
export interface FmvBrowseModel {
  skuId: string;
  modelName: string;
  displayName: string | null;
  assetClassId: string | null;
}

// pg returns NUMERIC columns as strings over JSON; stay defensive the same
// way MakeModelPicker.tsx's `usd()` does — a plain string has no
// .toLocaleString() override (falls through to Object.prototype's, which
// just returns the string unformatted, no error, no $ sign).
export function formatUsd(n: number | string | null | undefined): string | null {
  if (n == null) return null;
  const v = typeof n === 'number' ? n : Number(n);
  return Number.isFinite(v)
    ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : null;
}

/** One value, not a degenerate `$X-$X` range, when only one point exists.
 * Mirrors MakeModelPicker.tsx's `rangeText`. */
export function formatRangeText(low: number | string | null, high: number | string | null): string {
  if (low == null && high == null) return '—';
  if (low == null || high == null || low === high) return formatUsd(low ?? high) ?? '—';
  return `${formatUsd(low)}–${formatUsd(high)}`;
}

/** Distinct, sorted asset-class ids present among a make's product lines —
 * the chip row only renders when there is more than one to choose between
 * (caller's concern, not this function's). */
export function extractAssetClassIds(lines: readonly { assetClassId: string | null }[]): string[] {
  return [...new Set(lines.map((l) => l.assetClassId).filter((c): c is string => !!c))].sort();
}

/** Gate 1: nothing lists (product line, asset class chips, model field)
 * until a manufacturer is chosen. */
export function isManufacturerGateOpen(manufacturerId: string | null | undefined): boolean {
  return !!manufacturerId;
}

/** Gate 2: no models list until a product line is picked OR a specific
 * asset class is chosen — a manufacturer alone is too broad. */
export function isModelGateOpen(params: { activeLineId: string | null; assetClassId: string | null }): boolean {
  return !!params.activeLineId || !!params.assetClassId;
}

/** Product-line list narrowed to one asset class, when one is chosen. */
export function filterLinesByAssetClass<T extends { assetClassId: string | null }>(
  lines: readonly T[],
  assetClassId: string | null,
): T[] {
  return assetClassId ? lines.filter((l) => l.assetClassId === assetClassId) : [...lines];
}

/** Case-insensitive substring filter over a model/line name — the shared
 * "type to filter" predicate both the product-line select (while gate 2 is
 * shut) and the model list (once it's open) narrow against. */
export function filterByNeedle<T extends { modelName: string }>(items: readonly T[], needle: string): T[] {
  const n = needle.trim().toLowerCase();
  if (!n) return [...items];
  return items.filter((i) => i.modelName.toLowerCase().includes(n));
}

/** Model names carry the make as a prefix ("HPE ProLiant DL380"); once a
 * manufacturer is chosen it's redundant in the model list — strip it for
 * display. Mirrors MakeModelPicker.tsx's `stripMake`. */
export function stripMake(modelName: string, make: string): string {
  const m = make.trim();
  if (m && modelName.toLowerCase().startsWith(m.toLowerCase())) {
    return modelName.slice(m.length).replace(/^[\s-]+/, '') || modelName;
  }
  return modelName;
}

// ---------------------------------------------------------------------------
// Asset-class slug -> human label (public browse defect #1)
//
// Migration 844 deliberately does NOT project asset_class.name into
// v_sku_catalog_fmv_public (its header: "asset_class_id is already exposed
// and is a readable text slug; label client-side") — so both public
// consumers (the widget, and any future public surface) label the raw slug
// here, ONE definition, rather than each re-deriving (and drifting from) the
// authenticated portal's asset_class.name copy. This map is intentionally a
// literal copy of every asset_class.id/name pair seeded across
// db/schema/*.sql (200, 481, 526, 841, 1014) — it does not read the DB, since the
// whole point of this module is running in the public widget with zero
// privileged access. An id this map doesn't recognise (a class added after
// this file was last updated) degrades to a title-cased, underscore-free
// rendering of the slug itself — never the raw slug verbatim.
// ---------------------------------------------------------------------------
const ASSET_CLASS_LABELS: Record<string, string> = {
  gpu: 'GPU (Accelerator)',
  server: 'AI/HPC Server',
  ib_switch: 'InfiniBand Switch',
  ethernet_switch: 'Ethernet Switch',
  router: 'Router',
  nvlink_switch: 'NVLink Switch',
  storage: 'Storage',
  memory: 'Memory',
  cpu: 'CPU (Processor)',
  rack_system: 'Rack-Scale System',
  cloud_vm: 'Cloud VM',
  nic: 'Network Adapter',
  dpu: 'Data Processing Unit',
};

/** Title-cases an unrecognised slug ("liquid_cooling_cdu" -> "Liquid Cooling
 * Cdu") — never as polished as a curated label, but never a raw
 * underscored/lowercase identifier either. */
function titleCaseSlug(slug: string): string {
  return slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Human label for a public asset_class_id slug — every slug
 * v_sku_catalog_fmv_public can return is covered by `ASSET_CLASS_LABELS`
 * above; a slug added to the DB after this file was last updated (or any
 * other unrecognised input) degrades to a title-cased rendering rather than
 * ever surfacing the raw slug to a visitor. */
export function labelForAssetClass(assetClassId: string | null | undefined): string {
  if (!assetClassId) return 'Other';
  return ASSET_CLASS_LABELS[assetClassId] ?? titleCaseSlug(assetClassId);
}

// ---------------------------------------------------------------------------
// Product-line label disambiguation (public browse defect #2)
//
// Several root rows (`parentSkuId IS NULL`) can legitimately share the same
// stripped display name — most commonly a family master ("NVIDIA A100
// Family", strips to "A100 Family") sitting beside standalone variant SKUs
// of the same base part that were never linked into that family as children
// (e.g. four standalone "A100" SKUs — 40GB/80GB x PCIe/SXM4 — each with
// parent_sku_id NULL). That's a catalog-completeness gap, not something this
// module silently papers over by dropping rows; every row stays, but a
// visitor must be able to tell identically-named ones apart. `short_description`
// (already public — v_sku_catalog_fmv_public, migration 844's SELECT list)
// reliably distinguishes them ("NVIDIA A100 Tensor Core GPU 40GB SXM4" vs
// "...80GB PCIe") — used here ONLY for lines whose stripped name collides
// with a sibling's, so a non-ambiguous line's label is never touched.
// ---------------------------------------------------------------------------

/** Computes a display label per line (keyed by skuId) for a list of root
 * rows sharing one manufacturer. Non-colliding lines get the plain
 * `stripMake`d name, exactly as before. A line whose stripped name collides
 * with at least one sibling's gets its own `shortDescription` (also
 * stripMake'd) appended when that description is both present and actually
 * different from the base name; a colliding line with no usable description
 * falls back to a short id suffix — enough to prove it's a distinct row,
 * never re-merged with its sibling's label by accident. */
export function disambiguateLineLabels<
  T extends { skuId: string; modelName: string; shortDescription?: string | null },
>(lines: readonly T[], make: string): Map<string, string> {
  const withBase = lines.map((line) => ({ line, base: stripMake(line.modelName, make) }));

  const baseCounts = new Map<string, number>();
  for (const { base } of withBase) {
    baseCounts.set(base, (baseCounts.get(base) ?? 0) + 1);
  }

  const labels = new Map<string, string>();
  for (const { line, base } of withBase) {
    if ((baseCounts.get(base) ?? 0) <= 1) {
      labels.set(line.skuId, base);
      continue;
    }

    const desc = line.shortDescription?.trim();
    const strippedDesc = desc ? stripMake(desc, make) : null;
    const disambiguated =
      strippedDesc && strippedDesc.toLowerCase() !== base.toLowerCase()
        ? strippedDesc
        : `${base} (${line.skuId.slice(0, 8)})`;
    labels.set(line.skuId, disambiguated);
  }
  return labels;
}
